import { match } from 'ts-pattern';

import { IconProps } from '@eluve/components';

import { Citation } from './CitationText';

/**
 * Citation Source type to its readable labels
 */
const PATIENT_SOURCE_LABELS_MAPPING: Record<string, string> = {
  signedNotes: 'Chart',
  patientDetails: 'Patient Details',
  patientConditions: 'Conditions',
  observations: 'Observations',
  medicationRequests: 'Medication',
  carePlans: 'Care Plans',
  labResults: 'Lab Results',
  fullPatientDataContext: 'Full Patient Context',
  appointmentDetails: 'Appointment Details',
  allMedicalNotes: 'All Medical Notes',
  latestMedicalNote: 'Latest Medical Note',
  allPatientArtifacts: 'All Patient Artifacts',
  latestIntakeConversation: 'Latest Intake Conversation',
  patientOverviews: 'Patient Overviews',
  intakeForm: 'Intake Form',
  tempMedicalNotes: 'Temporary Medical Notes',
  uploadedFiles: 'Uploaded Files',
  unsignedNotes: 'Unsigned Notes',
} as const;

/**
 * Citation source type to icon mapping
 */
const PATIENT_SOURCE_ICONS_MAPPING: Record<string, IconProps['name']> = {
  signedNotes: 'FileText',
  patientDetails: 'FileText',
  patientConditions: 'Stethoscope',
  observations: 'Eye',
  medicationRequests: 'Pill',
  carePlans: 'HeartHandshake',
  labResults: 'FlaskConical',
  fullPatientDataContext: 'FileText',
  appointmentDetails: 'Calendar',
  allMedicalNotes: 'FileText',
  latestMedicalNote: 'FileText',
  allPatientArtifacts: 'FileText',
  latestIntakeConversation: 'FileText',
  patientOverviews: 'FileText',
  intakeForm: 'FileText',
  tempMedicalNotes: 'FileText',
  uploadedFiles: 'FileText',
  unsignedNotes: 'FileText',
} as const;

export function getSourceTypeLabel(sourceType?: string): string {
  if (!sourceType) {
    return 'Medical Record';
  }

  return PATIENT_SOURCE_LABELS_MAPPING[sourceType] ?? sourceType;
}

export function getSourceIcon(sourceType?: string): IconProps['name'] {
  if (!sourceType) {
    return 'FileText';
  }

  return PATIENT_SOURCE_ICONS_MAPPING[sourceType] ?? 'FileText';
}

export function parseCitedTextData(text: string) {
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed[0] : parsed;
  } catch {
    return null;
  }
}

/**
 * Generate URL to view original citation source
 */
export function getSourceUrl(
  citation: Citation,
  tenantId?: string,
  patientId?: string,
): string | null {
  if (!tenantId) {
    return null;
  }

  const jsonData = parseCitedTextData(citation.text);
  const sourceType = citation.source.sourceType;

  return match(sourceType)
    .with('signedNotes', 'unsignedNotes', () => {
      const appointmentId =
        jsonData?.appointmentId ?? jsonData?.[0]?.appointmentId;
      if (appointmentId) {
        return `/organizations/${tenantId}/appointments/${appointmentId}?tab=summary`;
      }

      if (patientId) {
        return `/organizations/${tenantId}/patients/${patientId}/artifacts`;
      }
      return null;
    })
    .otherwise(() => {
      if (patientId) {
        return `/organizations/${tenantId}/patients/${patientId}/artifacts`;
      }
      return null;
    });
}
